// src/pages/Pricing/Pricing.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useUser } from "./../../contexts/UserContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useNotification } from "./../../contexts/NotificationContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import {
  FaArrowRight,
  FaBolt,
  FaCheck,
  FaCrown,
  FaHeart,
  FaInfoCircle,
  FaRocket,
  FaShieldAlt,
  FaSync,
  FaTimes,
  FaTrophy,
  FaUsers,
} from "react-icons/fa";
import useScrollToTop from "../../hooks/useScrollToTop";
import { createCheckoutSessionForPlan } from "./../../services/paymentServices";
import { getUserById } from "./../../services/userServices";
import { sendNotificationToAdmin } from "./../../services/notificationServices";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PLANS = [
  {
    key: "demo",
    title: "Mode Démo",
    shortTitle: "Démo",
    price: "Gratuit",
    priceNote: "Découverte",
    description:
      "Testez l’application avec des données fictives pour découvrir le fonctionnement général.",
    features: [
      { text: "Accès aux principales fonctionnalités", included: true },
      { text: "Données fictives pour tester", included: true },
      { text: "Compétition active réelle", included: false },
      { text: "Résultats publics réels", included: false },
    ],
    icon: FaBolt,
    accent: "from-sky-500 to-indigo-500",
    ring: "ring-sky-200 dark:ring-sky-900/70",
    isPopular: false,
    buttonText: "Essayer la démo",
    role_id: 2,
    formule: "Mode Démo",
    product_id: null,
  },
  {
    key: "unit",
    title: "Achat à l’unité",
    shortTitle: "TournoiPro",
    price: "25€",
    priceNote: "par compétition",
    description:
      "Idéal pour les clubs qui organisent une ou deux compétitions dans l’année.",
    features: [
      { text: "Toutes les fonctionnalités activées", included: true },
      { text: "Accès à une compétition", included: true },
      { text: "Validité de la compétition : 3 mois", included: true },
      { text: "QR Code & résultats publics", included: true },
      { text: "Affichage live des résultats", included: true },
      { text: "Double affichage du tableau de score", included: true },
    ],
    icon: FaTrophy,
    accent: "from-emerald-500 to-teal-500",
    ring: "ring-emerald-200 dark:ring-emerald-900/70",
    isPopular: false,
    buttonText: "Acheter une compétition",
    role_id: 3,
    formule: "TournoiPro",
    product_id: "prod_SBXHPJz4UzdVWS",
  },
  {
    key: "annual",
    title: "Formule Annuelle",
    shortTitle: "TournoiUltim",
    price: "50€",
    priceNote: "par an",
    description:
      "Le meilleur choix pour utiliser l’application sans limite toute l’année.",
    features: [
      { text: "Toutes les fonctionnalités activées", included: true },
      { text: "Compétitions illimitées", included: true },
      { text: "Validité de la formule : 1 an", included: true },
      { text: "QR Code & résultats publics", included: true },
      { text: "Affichage live des résultats", included: true },
      { text: "Double affichage du tableau de score", included: true },
    ],
    icon: FaCrown,
    accent: "from-indigo-500 via-violet-500 to-pink-500",
    ring: "ring-violet-200 dark:ring-violet-900/70",
    isPopular: true,
    buttonText: "Choisir la formule annuelle",
    role_id: 4,
    formule: "TournoiUltim",
    product_id: "prod_SBXJMPao4E03Jk",
  },
];

const PAGE =
  "min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50";

export default function Pricing() {
  useScrollToTop();

  const { user, updateUser } = useUser();
  const { addNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [loadingPlanKey, setLoadingPlanKey] = useState(null);

  const userInfo = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userInfo"));
    } catch {
      return null;
    }
  }, []);

  const userId = userInfo?.userId;
  const currentRoleId = Number(userInfo?.role_id || user?.role_id || 0);
  const isAuthenticated = Boolean(userId);

  const userRole =
    user && user.role_id
      ? getRoleFromRoleId(user.role_id)
      : userInfo?.role_id
      ? getRoleFromRoleId(userInfo.role_id)
      : "Non connecté";

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("userInfo"));

    if (!storedUser || !storedUser.userId) {
      localStorage.removeItem("userInfo");
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.get("success") === "true") {
      addNotification(
        "Transaction réussie ! Cliquez sur “Rafraîchir la formule” pour mettre à jour votre accès.",
        "success"
      );
      navigate("/formules-tarifs", { replace: true });
    }

    if (searchParams.get("canceled") === "true") {
      addNotification("Transaction annulée.", "warning");
      navigate("/formules-tarifs", { replace: true });
    }
  }, [location, addNotification, navigate]);

  const handlePurchase = async (plan) => {
    if (!isAuthenticated) {
      navigate("/register");
      return;
    }

    if (plan.key === "demo") {
      addNotification(
        "Le mode démo est disponible à la création du compte. Votre compte est déjà connecté.",
        "info"
      );
      return;
    }

    if (!userId) {
      addNotification(
        "Impossible de récupérer votre ID utilisateur. Veuillez vous reconnecter.",
        "error"
      );
      return;
    }

    if (!plan.role_id || !plan.formule || !plan.price || !plan.product_id) {
      addNotification("Produit ou plan invalide. Veuillez réessayer.", "error");
      return;
    }

    try {
      setLoading(true);
      setLoadingPlanKey(plan.key);

      const checkoutUrl = await createCheckoutSessionForPlan(
        {
          role_id: plan.role_id,
          formule: plan.formule,
          price: plan.price.replace("€", "").trim(),
          product_id: plan.product_id,
        },
        userId
      );

      const token = localStorage.getItem("token");
      const message = `L'utilisateur ${userInfo?.prenom || ""} ${
        userInfo?.nom || ""
      } a tenté de souscrire à la formule ${plan.formule} pour ${plan.price}.`;

      sendNotificationToAdmin(message, token);

      window.location.href = checkoutUrl;
    } catch (error) {
      console.error("Erreur lors de la création de la session :", error);
      addNotification(
        "Erreur lors de la création de la session de paiement. Veuillez réessayer.",
        "error"
      );
    } finally {
      setLoading(false);
      setLoadingPlanKey(null);
    }
  };

  const handleRefresh = async () => {
    if (!userId) {
      addNotification(
        "Impossible de rafraîchir les données. Veuillez vous reconnecter.",
        "error"
      );
      return;
    }

    try {
      setLoading(true);

      const updatedUser = await getUserById(userId);

      if (updatedUser) {
        updateUser(updatedUser);
        localStorage.setItem("userInfo", JSON.stringify(updatedUser));

        addNotification(
          "Votre formule a été mise à jour avec succès.",
          "success"
        );

        window.location.reload();
      } else {
        addNotification("Utilisateur non trouvé.", "error");
      }
    } catch (error) {
      console.error("Erreur lors du rafraîchissement :", error);
      addNotification(
        "Erreur lors du rafraîchissement des données. Veuillez réessayer.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const getPlanState = (plan) => {
    const isCurrent = Number(plan.role_id) === Number(currentRoleId);

    if (!isAuthenticated) {
      return {
        isCurrent: false,
        disabled: false,
        label: "Créer un compte",
        helper: "Connexion nécessaire",
      };
    }

    if (plan.key === "demo") {
      return {
        isCurrent,
        disabled: true,
        label: isCurrent ? "Formule active" : "Indisponible",
        helper: "Réservé à la découverte",
      };
    }

    if (plan.key === "unit" && currentRoleId === 4) {
      return {
        isCurrent: false,
        disabled: true,
        label: "Inclus dans l’annuel",
        helper: "Vous avez déjà mieux",
      };
    }

    if (plan.key === "unit" && currentRoleId === 3) {
      return {
        isCurrent: true,
        disabled: false,
        label: "Acheter une autre compétition",
        helper: "Renouvellement possible",
      };
    }

    if (plan.key === "annual" && currentRoleId === 4) {
      return {
        isCurrent: true,
        disabled: false,
        label: "Renouveler l’annuel",
        helper: "Prolongez votre accès",
      };
    }

    return {
      isCurrent,
      disabled: false,
      label: plan.buttonText,
      helper: plan.isPopular ? "Recommandé" : "Paiement sécurisé",
    };
  };

  return (
    <div className={PAGE}>
      <main className="mx-auto w-full max-w-[1280px] px-3 pb-12 pt-[120px] sm:px-5 md:pt-[145px]">
        <div className="space-y-8">
          <PricingHero
            userRole={userRole}
            isAuthenticated={isAuthenticated}
            loading={loading}
            onRefresh={handleRefresh}
          />

          <PricingTrustBar />

          <section className="grid grid-cols-1 gap-5 lg:grid-cols-3 lg:items-stretch">
            {PLANS.map((plan) => {
              const state = getPlanState(plan);

              return (
                <PricingCard
                  key={plan.key}
                  plan={plan}
                  state={state}
                  loading={loadingPlanKey === plan.key}
                  onPurchase={() => handlePurchase(plan)}
                />
              );
            })}
          </section>

          <PricingHelpBox />

          <DonationBox />
        </div>
      </main>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

function PricingHero({ userRole, isAuthenticated, loading, onRefresh }) {
  return (
    <section className="overflow-hidden rounded-[36px] bg-slate-950 shadow-xl dark:bg-slate-900">
      <div className="relative p-5 sm:p-8 lg:p-10">
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-indigo-500/25 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-52 w-52 rounded-full bg-pink-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/15 blur-3xl" />

        <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-center">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/80">
              <FaRocket />
              Formules & tarifs
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-white sm:text-5xl">
              Choisissez la formule adaptée à votre tournoi
            </h1>

            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-300 sm:text-base">
              Démo gratuite, achat ponctuel ou formule annuelle : sélectionnez
              l’accès qui correspond au nombre de compétitions que vous
              organisez.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <HeroBadge icon={FaShieldAlt} text="Paiement sécurisé Stripe" />
              <HeroBadge icon={FaTrophy} text="Pensé pour le Judo" />
              <HeroBadge icon={FaUsers} text="Résultats publics & QR Code" />
            </div>
          </div>

          <div className="rounded-[28px] border border-white/10 bg-white/[0.08] p-5 text-white shadow-sm backdrop-blur">
            <div className="text-xs font-bold uppercase tracking-wide text-white/55">
              Votre formule actuelle
            </div>

            <div className="mt-2 text-2xl font-bold">
              {userRole || "Non connecté"}
            </div>

            <p className="mt-2 text-sm leading-relaxed text-white/60">
              Après un paiement Stripe, rafraîchissez votre formule pour mettre
              à jour vos droits d’accès.
            </p>

            {isAuthenticated ? (
              <button
                type="button"
                onClick={onRefresh}
                disabled={loading}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-bold text-slate-950 transition hover:-translate-y-0.5 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSync className={loading ? "animate-spin" : ""} />
                {loading ? "Chargement..." : "Rafraîchir la formule"}
              </button>
            ) : (
              <div className="mt-5 rounded-2xl bg-white/10 p-3 text-sm font-semibold text-white/80">
                Connectez-vous ou créez un compte pour souscrire.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroBadge({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold text-white/80 backdrop-blur">
      <Icon />
      {text}
    </span>
  );
}

function PricingTrustBar() {
  return (
    <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <TrustItem
        icon={FaCheck}
        title="Sans engagement compliqué"
        text="Choisissez uniquement ce dont vous avez besoin."
      />
      <TrustItem
        icon={FaTrophy}
        title="Spécial compétition"
        text="Gestion des pesées, poules, tapis, combats et résultats."
      />
      <TrustItem
        icon={FaShieldAlt}
        title="Accès sécurisé"
        text="Les formules activent les droits de votre compte."
      />
    </section>
  );
}

function TrustItem({ icon: Icon, title, text }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300">
          <Icon />
        </div>

        <div>
          <h3 className="font-bold text-slate-900 dark:text-white">{title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
            {text}
          </p>
        </div>
      </div>
    </div>
  );
}

function PricingCard({ plan, state, loading, onPurchase }) {
  const Icon = plan.icon;

  return (
    <article
      className={`group relative flex h-full min-h-[620px] flex-col overflow-hidden rounded-[32px] border bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-2xl dark:bg-slate-900 ${
        plan.isPopular
          ? `border-violet-300 ring-4 ${plan.ring}`
          : "border-slate-200 dark:border-slate-800"
      }`}
    >
      {plan.isPopular ? (
        <div className="absolute right-5 top-5 z-10 rounded-full bg-gradient-to-r from-indigo-600 to-pink-500 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white shadow-lg">
          Populaire
        </div>
      ) : null}

      {state.isCurrent ? (
        <div className="absolute left-5 top-5 z-10 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white shadow-lg">
          Votre formule
        </div>
      ) : null}

      <div
        className={`absolute inset-x-0 top-0 h-2 bg-gradient-to-r ${plan.accent}`}
      />

      <div className="pt-7">
        <div
          className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${plan.accent} text-xl text-white shadow-lg transition group-hover:scale-105`}
        >
          <Icon />
        </div>

        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          {plan.title}
        </h2>

        <p className="mt-2 min-h-[48px] text-sm leading-relaxed text-slate-500 dark:text-slate-400">
          {plan.description}
        </p>

        <div className="mt-6 rounded-[26px] bg-slate-50 p-5 dark:bg-slate-950/60">
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
              {plan.price}
            </span>
            <span className="pb-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
              {plan.priceNote}
            </span>
          </div>

          <div className="mt-2 text-xs font-bold uppercase tracking-wide text-slate-400">
            {plan.shortTitle}
          </div>
        </div>
      </div>

      <ul className="mt-6 flex-1 space-y-3">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <span
              className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${
                feature.included
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/70 dark:text-emerald-300"
                  : "bg-red-100 text-red-700 dark:bg-red-950/70 dark:text-red-300"
              }`}
            >
              {feature.included ? <FaCheck /> : <FaTimes />}
            </span>

            <span className="text-sm font-medium leading-relaxed text-slate-700 dark:text-slate-200">
              {feature.text}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-6">
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
          <FaInfoCircle />
          {state.helper}
        </div>

        <button
          type="button"
          onClick={onPurchase}
          disabled={state.disabled || loading}
          className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3.5 text-sm font-bold transition disabled:cursor-not-allowed disabled:opacity-60 ${
            state.disabled
              ? "bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-500"
              : plan.isPopular
              ? "bg-gradient-to-r from-indigo-600 to-pink-500 text-white shadow-lg hover:-translate-y-0.5"
              : "bg-slate-950 text-white hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
          }`}
        >
          {loading ? "Redirection..." : state.label}
          {!state.disabled && !loading ? <FaArrowRight /> : null}
        </button>
      </div>
    </article>
  );
}

function PricingHelpBox() {
  return (
    <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Quelle formule choisir ?
          </h2>

          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-500 dark:text-slate-400">
            Pour une compétition ponctuelle, l’achat à l’unité suffit. Pour un
            club ou une organisation qui gère plusieurs événements dans l’année,
            la formule annuelle est la plus avantageuse.
          </p>
        </div>

        <div className="rounded-2xl bg-indigo-50 px-4 py-3 text-sm font-bold text-indigo-700 dark:bg-indigo-950/70 dark:text-indigo-300">
          2 compétitions ou plus ? L’annuel est plus rentable.
        </div>
      </div>
    </section>
  );
}

function DonationBox() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="relative p-5 sm:p-6">
        <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-pink-500/10 blur-3xl" />

        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-pink-50 text-pink-600 dark:bg-pink-950/70 dark:text-pink-300">
              <FaHeart />
            </div>

            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Merci pour votre soutien
              </h2>

              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-500 dark:text-slate-400">
                MonAppliTournoi évolue grâce aux retours des clubs et des
                organisateurs. Chaque abonnement aide à maintenir et améliorer
                l’application.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}